import React from 'react';
import { IdentificationBadge, Sparkle, UsersThree } from '@phosphor-icons/react';
import DashboardModal from '../../DashboardModal';
import { ContactsSnapshotCard } from '../..';
import { OBD_DASHBOARD_MODAL_Z } from '../../oneButtonOverlayZ';
import { useOneButtonStore } from '../../../../../../store/oneButtonStore';
import { opportunityHasSyncedTitleContact } from '../../../../../../helpers/titleContactStatus';

export type DashboardV3SectionModalsProps = {
    theme: 'dark' | 'light';
    /** V2 = redesigned split contacts modal; V3 = legacy card-in-modal (unchanged). */
    dashboardUiVariant?: 'v2' | 'v3';
    contactsOpen: boolean;
    onCloseContacts: () => void;
    aiDesignsOpen: boolean;
    onCloseAiDesigns: () => void;
    designsDashboardSlot: React.ReactNode;
    contactsProps: {
        loading: boolean;
        opportunityId: string;
        isTitleContactExist: boolean | null;
        onAddTitleContactsClick: () => void;
        onBlackKnightTitleCheckClick: () => void;
    };
};

export const DashboardV3SectionModals: React.FC<DashboardV3SectionModalsProps> = ({
    theme,
    dashboardUiVariant = 'v3',
    contactsOpen,
    onCloseContacts,
    aiDesignsOpen,
    onCloseAiDesigns,
    designsDashboardSlot,
    contactsProps,
}) => {
    const modalClass = `obd-sync-modal obd-theme-${theme}`;
    const modalRoot = `obd-sync-modal-root obd-theme-${theme}`;
    const isV2Contacts = dashboardUiVariant === 'v2';
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const titleContactSynced = opportunityHasSyncedTitleContact(opportunityDetails);

    return (
        <>
            <DashboardModal
                open={contactsOpen}
                onClose={onCloseContacts}
                title={
                    <>
                        <UsersThree weight="duotone" aria-hidden />
                        <span>Contacts</span>
                    </>
                }
                width={isV2Contacts ? 'min(96vw, 1140px)' : 'min(96vw, 1120px)'}
                centered
                zIndex={OBD_DASHBOARD_MODAL_Z}
                theme={theme}
                className={
                    isV2Contacts
                        ? `${modalClass} obd-v2-modal--contacts obd-v2-modal--contacts-split`
                        : `${modalClass} obd-v3-modal--contacts`
                }
                rootClassName={modalRoot}
                loading={contactsProps.loading}
                footer={
                    isV2Contacts ? (
                        <div className="obd-dashboard-modal__contacts-footer-bar">
                            <button
                                type="button"
                                className="obd-dashboard-modal__contacts-footer-close"
                                onClick={onCloseContacts}
                            >
                                Close
                            </button>
                            {contactsProps.isTitleContactExist !== null && !titleContactSynced ? (
                                <button
                                    type="button"
                                    className="obd-dashboard-modal__contacts-footer-close obd-dashboard-modal__contacts-footer-title"
                                    aria-label="Add title contacts"
                                    onClick={
                                        contactsProps.isTitleContactExist === true
                                            ? contactsProps.onAddTitleContactsClick
                                            : contactsProps.onBlackKnightTitleCheckClick
                                    }
                                >
                                    <IdentificationBadge size={15} weight="bold" aria-hidden />
                                    Add title
                                </button>
                            ) : null}
                            <div
                                id="obd-contacts-panel-footer-actions"
                                className="obd-dashboard-modal__contacts-footer-actions"
                            />
                        </div>
                    ) : undefined
                }
            >
                {contactsOpen ? (
                    <div
                        className={
                            isV2Contacts
                                ? 'obd-v2-modal-body obd-v2-modal-body--contacts obd-v2-modal-body--contacts-split'
                                : 'obd-v3-modal-body obd-v3-modal-body--contacts'
                        }
                    >
                        <ContactsSnapshotCard
                            {...contactsProps}
                            layout={isV2Contacts ? 'modalSplit' : 'card'}
                        />
                    </div>
                ) : null}
            </DashboardModal>

            <DashboardModal
                open={aiDesignsOpen && !!designsDashboardSlot}
                onClose={onCloseAiDesigns}
                title={
                    <>
                        <Sparkle weight="duotone" aria-hidden />
                        <span>AI Designs</span>
                    </>
                }
                width="min(96vw, 1200px)"
                centered
                zIndex={OBD_DASHBOARD_MODAL_Z + 1}
                theme={theme}
                className={`${modalClass} obd-v3-modal--ai-designs`}
                rootClassName={modalRoot}
            >
                <div className="obd-v3-modal-body obd-v3-modal-body--ai">{designsDashboardSlot}</div>
            </DashboardModal>
        </>
    );
};
